import { useState } from 'react';
import { format, parseISO, startOfDay } from 'date-fns';
import type { TimelineEvent, FilterState } from '../types';
import { useEvents } from '../hooks/useEvents';
import { fetchAllEventsForPrint } from '../api/client';
import TimelineEventItem from './TimelineEventItem';
import EventDetail from './EventDetail';

function printTimeline(events: TimelineEvent[]) {
  const grouped = new Map<string, TimelineEvent[]>();
  for (const e of events) {
    const day = format(startOfDay(parseISO(e.timestamp)), 'yyyy-MM-dd');
    if (!grouped.has(day)) grouped.set(day, []);
    grouped.get(day)!.push(e);
  }

  const formatRecipients = (list?: Array<{ name: string; address: string }>) => {
    if (!list?.length) return '—';
    return list.map((r) => r.name || r.address).join(', ');
  };

  const cards = Array.from(grouped.entries()).map(([day, dayEvents]) => {
    const dayLabel = format(parseISO(day), 'EEEE, MMMM d, yyyy');
    const eventCards = dayEvents.map((e) => {
      const attachments = e.attachments?.length
        ? `<div class="section"><span class="label">Attachments</span>
            <ul class="attach-list">${e.attachments.map((a) =>
              `<li>📎 ${a.name}${a.size ? ` (${Math.round(a.size / 1024)} KB)` : ''}</li>`
            ).join('')}</ul></div>`
        : '';

      const bodyContent = (e as any).body_text || e.body_preview;
      const body = bodyContent
        ? `<div class="section"><span class="label">Body</span><div class="body-text">${bodyContent}</div></div>`
        : '';

      return `<div class="email-card">
        <div class="card-header">
          <span class="type-badge">${e.type}</span>
          <span class="time">${format(parseISO(e.timestamp), 'h:mm a')}</span>
          ${e.folder ? `<span class="folder">📁 ${e.folder}</span>` : ''}
        </div>
        <div class="subject">${e.subject || e.title || '(No subject)'}</div>
        <table class="meta-table">
          <tr><td class="label">From</td><td>${e.from_name || e.from_address || e.actor}</td></tr>
          ${e.to_recipients?.length ? `<tr><td class="label">To</td><td>${formatRecipients(e.to_recipients)}</td></tr>` : ''}
          ${e.cc_recipients?.length ? `<tr><td class="label">CC</td><td>${formatRecipients(e.cc_recipients)}</td></tr>` : ''}
        </table>
        ${body}
        ${attachments}
      </div>`;
    }).join('');

    return `<div class="day-group">
      <div class="day-header">${dayLabel} <span class="day-count">${dayEvents.length} email${dayEvents.length !== 1 ? 's' : ''}</span></div>
      ${eventCards}
    </div>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html><head><title>Outlook Audit Timeline</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, sans-serif; padding: 24px; color: #111; font-size: 13px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .meta { font-size: 12px; color: #6b7280; margin: 0 0 20px; }
  .day-group { margin-bottom: 24px; }
  .day-header { font-size: 14px; font-weight: 700; background: #f3f4f6; padding: 8px 12px;
    border-left: 4px solid #1e3a5f; margin-bottom: 8px; display: flex; justify-content: space-between; }
  .day-count { font-weight: 400; color: #6b7280; font-size: 12px; }
  .email-card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px;
    margin-bottom: 10px; page-break-inside: avoid; }
  .card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .type-badge { background: #e5e7eb; border-radius: 4px; padding: 1px 7px; font-size: 11px;
    font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
  .time { font-size: 12px; color: #6b7280; }
  .folder { font-size: 11px; color: #6b7280; margin-left: auto; }
  .subject { font-size: 15px; font-weight: 700; margin-bottom: 8px; }
  .meta-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  .meta-table td { padding: 2px 6px; font-size: 12px; vertical-align: top; }
  .meta-table td:first-child { width: 50px; }
  .label { font-weight: 600; color: #6b7280; font-size: 11px; text-transform: uppercase; }
  .section { margin-top: 8px; }
  .body-text { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;
    padding: 8px 10px; font-size: 12px; color: #374151; line-height: 1.5; white-space: pre-wrap; }
  .attach-list { margin: 4px 0 0 0; padding-left: 16px; font-size: 12px; color: #374151; }
  .attach-list li { margin-bottom: 2px; }
  @media print { @page { margin: 1.5cm; } .email-card { page-break-inside: avoid; } }
</style></head>
<body>
  <h1>📬 Outlook Audit — Email Timeline</h1>
  <p class="meta">Printed ${format(new Date(), 'PPpp')} &nbsp;·&nbsp; ${events.length} events</p>
  ${cards}
</body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

interface Props {
  filters: FilterState;
}

// Group events by calendar day
function groupByDay(events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const map = new Map<string, TimelineEvent[]>();
  for (const event of events) {
    const day = format(startOfDay(parseISO(event.timestamp)), 'yyyy-MM-dd');
    if (!map.has(day)) map.set(day, []);
    map.get(day)!.push(event);
  }
  return map;
}

export default function Timeline({ filters }: Props) {
  const { events, total, loading, error, page, setPage, refresh } = useEvents(filters);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [search, setSearch] = useState('');
  const [printing, setPrinting] = useState(false);

  function isExcluded(e: TimelineEvent): boolean {
    const body = ((e as any).body_text || e.body_preview || '').toLowerCase();
    return body.includes('ryse-electric.com') || body.includes('angel');
  }

  function isBounce(e: TimelineEvent): boolean {
    const subject = (e.subject || e.title || '').toLowerCase();
    const from = (e.from_address || e.actor || '').toLowerCase();
    return (
      subject.startsWith('undeliverable:') ||
      subject.startsWith('delivery status notification') ||
      subject.startsWith('mail delivery failed') ||
      subject.startsWith('mail delivery failure') ||
      subject.startsWith('returned mail:') ||
      subject.startsWith('failure notice') ||
      subject.includes('undeliverable') ||
      from.startsWith('postmaster@') ||
      from.startsWith('mailer-daemon@')
    );
  }

  async function handlePrint() {
    setPrinting(true);
    try {
      const allEvents = await fetchAllEventsForPrint(filters);
      // Deduplicate: one entry per email (drop attachment-added sub-events)
      const seen = new Set<string>();
      const deduped = allEvents.filter((e) => {
        const key = (e as any).source_email_id || e.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const toprint = deduped.filter((e) => !isBounce(e) && !isExcluded(e));
      const finalPrint = search
        ? toprint.filter(
            (e) =>
              e.title.toLowerCase().includes(search.toLowerCase()) ||
              e.actor.toLowerCase().includes(search.toLowerCase()) ||
              e.description?.toLowerCase().includes(search.toLowerCase())
          )
        : toprint;
      printTimeline(finalPrint);
    } finally {
      setPrinting(false);
    }
  }

  const PAGE_SIZE = 50;

  const filtered = search
    ? events.filter(
        (e) =>
          e.title.toLowerCase().includes(search.toLowerCase()) ||
          e.actor.toLowerCase().includes(search.toLowerCase()) ||
          e.description?.toLowerCase().includes(search.toLowerCase())
      )
    : events;

  const grouped = groupByDay(filtered);

  return (
    <div style={styles.container}>
      {/* ── Timeline column ── */}
      <div style={styles.timelineCol}>
        {/* Search bar */}
        <div style={styles.toolbar}>
          <input
            style={styles.searchInput}
            placeholder="Search within results…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button style={styles.refreshBtn} onClick={refresh} title="Refresh">↻</button>
          <button style={styles.printBtn} onClick={handlePrint} disabled={printing} title="Print timeline">
            {printing ? 'Loading…' : '🖨 Print'}
          </button>
          <span style={styles.countBadge}>{total.toLocaleString()} events</span>
        </div>

        {/* Loading / error states */}
        {loading && <div style={styles.state}>Loading…</div>}
        {error && <div style={{ ...styles.state, color: '#dc2626' }}>Error: {error}</div>}

        {/* Grouped timeline */}
        {!loading && !error && filtered.length === 0 && (
          <div style={styles.state}>No events found. Adjust filters or trigger a manual ingest.</div>
        )}

        <div style={styles.eventList}>
          {Array.from(grouped.entries()).map(([day, dayEvents]) => (
            <div key={day}>
              <div style={styles.dayHeader}>
                {format(parseISO(day), 'EEEE, MMMM d, yyyy')}
                <span style={styles.dayCount}>{dayEvents.length}</span>
              </div>
              {dayEvents.map((event) => (
                <TimelineEventItem
                  key={event.id}
                  event={event}
                  isSelected={selectedEvent?.id === event.id}
                  onSelect={setSelectedEvent}
                  onPinToggle={refresh}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
            </span>
            <button
              style={styles.pageBtn}
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Detail panel ── */}
      {selectedEvent && (
        <EventDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flex: 1, overflow: 'hidden' },
  timelineCol: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff',
  },
  searchInput: {
    flex: 1, padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13,
  },
  refreshBtn: {
    padding: '6px 10px', background: '#f3f4f6', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 16,
  },
  printBtn: {
    padding: '6px 12px', background: '#f3f4f6', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
  },
  countBadge: {
    fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap',
  },
  state: { padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 14 },
  eventList: { flex: 1, overflowY: 'auto' },
  dayHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 16px', background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb',
    fontSize: 13, fontWeight: 600, color: '#374151', position: 'sticky', top: 0,
  },
  dayCount: {
    background: '#e5e7eb', borderRadius: 12,
    padding: '1px 8px', fontSize: 11, color: '#374151',
  },
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 12, padding: '12px 16px', borderTop: '1px solid #e5e7eb',
  },
  pageBtn: {
    padding: '6px 14px', border: '1px solid #d1d5db',
    borderRadius: 6, cursor: 'pointer', fontSize: 13, background: '#fff',
  },
};
